import { useEffect, useRef, useMemo, useState } from 'react';
import { FormatType, NoteFormatResponse } from '../types';
import { fetchFormatTypes, fetchSharedNoteFormats } from '../services/noteFormatService';
import useNoteFormatDisplayStore from '@/stores/noteFormatDisplayStore';
import { errorReporter } from '@/services/errorReportingService';

/**
 * Read-only variant of useNoteFormats for the Shared page.
 * Fetches formats via the public /shared/{token}/formats endpoint (no auth required).
 * Does NOT support generating, regenerating, or deleting formats.
 */
export const useSharedNoteFormats = (token: string): { isLoading: boolean } => {
  const [isLoading, setIsLoading] = useState(true);
  const cancelledRef = useRef(false);

  const setStoreGeneratedFormats = useNoteFormatDisplayStore((s) => s.setGeneratedFormats);
  const setStoreDisplayNameMap = useNoteFormatDisplayStore((s) => s.setDisplayNameMap);
  const setActiveTab = useNoteFormatDisplayStore((s) => s.setActiveTab);
  const clearActiveFormat = useNoteFormatDisplayStore((s) => s.clearActiveFormat);

  // Cleanup on unmount — mirrors useNoteFormats cleanup
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      clearActiveFormat();
      setStoreGeneratedFormats([]);
      setStoreDisplayNameMap({});
      setActiveTab('original');
    };
  }, [clearActiveFormat, setStoreGeneratedFormats, setStoreDisplayNameMap, setActiveTab]);

  // Fetch formats via public endpoint and format types, sync to store
  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [formats, types] = await Promise.all([
          fetchSharedNoteFormats(token).catch((error: unknown) => {
            // HTTP/JSON errors reported inside the service; network-level
            // rejections (offline, DNS, CORS) only surface here.
            if (error instanceof TypeError) {
              errorReporter.expected('Network error fetching shared note formats', error, {
                feature: 'noteFormats',
                operation: 'loadSharedFormats',
                tokenPrefix: token.slice(0, 8),
              });
            }
            return [] as NoteFormatResponse[];
          }),
          fetchFormatTypes().catch((error: unknown) => {
            errorReporter.expected('Failed to load format types for shared view', error, {
              feature: 'noteFormats',
              operation: 'loadSharedFormatTypes',
            });
            return [] as FormatType[];
          }),
        ]);

        if (!cancelled && !cancelledRef.current) {
          setStoreGeneratedFormats(formats);

          const map: Record<string, string> = {};
          for (const t of types) {
            map[t.prompt_value] = t.display_name;
          }
          setStoreDisplayNameMap(map);
        }
      } finally {
        if (!cancelled && !cancelledRef.current) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token, setStoreGeneratedFormats, setStoreDisplayNameMap]);

  return useMemo(() => ({ isLoading }), [isLoading]);
};
